'use client'

import * as React from 'react'
import { useRouter } from 'next/navigation'
import {
  TemplatePickerModal,
  type TemplatePickerTab,
} from '@/components/funnel/templates/TemplatePickerModal'

type OpenFunnelTemplatePickerOptions = {
  initialTab?: TemplatePickerTab
  onCreated?: (funnelId: number) => void
}

type FunnelTemplatePickerContextValue = {
  openFunnelTemplatePicker: (options?: OpenFunnelTemplatePickerOptions) => void
  closeFunnelTemplatePicker: () => void
}

const FunnelTemplatePickerContext =
  React.createContext<FunnelTemplatePickerContextValue | null>(null)

export function FunnelTemplatePickerProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const router = useRouter()

  const [open, setOpen] = React.useState(false)
  const [options, setOptions] = React.useState<OpenFunnelTemplatePickerOptions>({
    initialTab: 'official',
  })

  const openFunnelTemplatePicker = React.useCallback(
    (nextOptions?: OpenFunnelTemplatePickerOptions) => {
      setOptions({
        initialTab: nextOptions?.initialTab || 'official',
        onCreated: nextOptions?.onCreated,
      })

      setOpen(true)
    },
    []
  )

  const closeFunnelTemplatePicker = React.useCallback(() => {
    setOpen(false)
  }, [])

  const value = React.useMemo(
    () => ({
      openFunnelTemplatePicker,
      closeFunnelTemplatePicker,
    }),
    [openFunnelTemplatePicker, closeFunnelTemplatePicker]
  )

  return (
    <FunnelTemplatePickerContext.Provider value={value}>
      {children}

      <TemplatePickerModal
        open={open}
        initialTab={options.initialTab || 'official'}
        onOpenChange={setOpen}
        onCreated={(funnelId) => {
          setOpen(false)

          if (options.onCreated) {
            options.onCreated(funnelId)
            return
          }

          router.push(`/funnel-builder?funnelId=${funnelId}`)
          router.refresh()
        }}
      />
    </FunnelTemplatePickerContext.Provider>
  )
}

export function useFunnelTemplatePicker() {
  const ctx = React.useContext(FunnelTemplatePickerContext)

  if (!ctx) {
    throw new Error(
      'useFunnelTemplatePicker must be used inside FunnelTemplatePickerProvider'
    )
  }

  return ctx
}